/* eslint-disable i18next/no-literal-string */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { useCurrentTheme } from '@platform/theme-provider';
import { getFeatureFlags, getLDClient } from '@platform/ui-config';
import Button from '@radius/ui-library/dist/Button';
import Card from '@radius/ui-library/dist/Card';
import Checkbox from '@radius/ui-library/dist/Checkbox';
import Heading from '@radius/ui-library/dist/Heading';
import Row from '@radius/ui-library/dist/Row';
import SelectSingle from '@radius/ui-library/dist/SelectSingle';
import type { SelectSingleOption } from '@radius/ui-library/dist/SelectSingle';
import Tag from '@radius/ui-library/dist/Tag';
import Tooltip from '@radius/ui-library/dist/Tooltip';
import {
	DEFAULT_LANGUAGE_TAG,
	PSEUDO_LANGUAGE_TAG,
	SUPPORTED_LANGUAGES,
} from '@radius/ui-utils';
import i18n from 'i18next';
import styled from 'styled-components';

import {
	I18N_PSEUDO_WRAPPED_KEY,
	isI18nDebugActive,
} from '../../Auxillary/i18nDebug';
import { I18N_LANGUAGE_STORAGE_KEY } from '../../i18n';

const StyledPanelWrapper = styled.div<{ $isCollapsed: boolean }>`
	bottom: 1rem;
	position: fixed;
	left: 50%;
	transform: translateX(-50%);
	width: 15rem;
	z-index: 99999;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3);

	${({ $isCollapsed }) =>
		$isCollapsed &&
		`
		[class*='CardBody__StyledCardBody'] {
			display: none;
		}
	`}
`;

const StyledCardContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const StyledApplyButton = styled(Button)`
	width: fit-content;
`;

interface LanguageOption {
	value: string;
	label: string;
}

const AVAILABLE_LANGUAGES: ReadonlyArray<string> = [
	DEFAULT_LANGUAGE_TAG,
	PSEUDO_LANGUAGE_TAG,
	'es-419',
	'pt-BR',
];

const LANGUAGE_OPTIONS: Array<LanguageOption> = [
	...SUPPORTED_LANGUAGES.filter((tag) =>
		AVAILABLE_LANGUAGES.includes(tag),
	).map((tag) => ({ value: tag, label: tag })),
	{ value: 'cimode', label: 'cimode - Show keys' },
];

const getFlag = (key: string): boolean => localStorage.getItem(key) === 'true';

const setFlag = (key: string, value: boolean): void => {
	localStorage.setItem(key, String(value));
};

const isPseudo = (lng: string): boolean => lng === PSEUDO_LANGUAGE_TAG;
const isCiMode = (lng: string): boolean => lng === 'cimode';

const I18N_CIMODE_NS_KEY = 'i18n-cimode-ns';

// LaunchDarkly flag that gates language configuration. The panel renders
// outside the LD React provider, so it reads the global LD client
// singleton (set by `InitializeFeatureFlags`) instead of `useFeatureFlags`.
const ENABLE_LANGUAGE_CONFIGURATION_FLAG = 'enableLanguageConfiguration';

const I18nDevPanel: FC = () => {
	const [isVisible, setIsVisible] = useState(isI18nDebugActive);
	const [isCollapsed, setIsCollapsed] = useState(true);
	const [isWrapped, setIsWrapped] = useState(() =>
		getFlag(I18N_PSEUDO_WRAPPED_KEY),
	);
	const [showNamespaces, setShowNamespaces] = useState(
		() => getFlag(I18N_CIMODE_NS_KEY) ?? true,
	);
	const [currentLng, setCurrentLng] = useState(
		() => i18n.resolvedLanguage || i18n.language,
	);
	const [isLanguageConfigEnabled, setIsLanguageConfigEnabled] =
		useState(false);
	const currentTheme = useCurrentTheme();
	const isPseudoActive = isPseudo(currentLng);
	const isCiModeActive = isCiMode(currentLng);

	useEffect(() => {
		const handleLanguageChanged = (lng: string): void => {
			setCurrentLng(lng);
		};

		i18n.on('languageChanged', handleLanguageChanged);

		return () => {
			i18n.off('languageChanged', handleLanguageChanged);
		};
	}, []);

	useEffect(() => {
		let isMounted = true;

		const syncFlag = async (): Promise<void> => {
			const flags = (await getFeatureFlags()) as Record<string, unknown>;

			if (isMounted) {
				setIsLanguageConfigEnabled(
					Boolean(flags[ENABLE_LANGUAGE_CONFIGURATION_FLAG]),
				);
			}
		};

		void syncFlag();

		// Reflect live flag changes streamed from LaunchDarkly.
		const ldClient = getLDClient();

		ldClient?.on('change', syncFlag);

		return () => {
			isMounted = false;
			ldClient?.off('change', syncFlag);
		};
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.ctrlKey && event.shiftKey && event.key === 'I') {
				event.preventDefault();
				setIsVisible((prev) => !prev);
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	const handleClose = useCallback(() => {
		setIsVisible(false);
	}, []);

	const handleToggleCollapsed = useCallback(() => {
		setIsCollapsed((prev) => !prev);
	}, []);

	const handleToggleWrapped = useCallback(() => {
		setIsWrapped((prev) => !prev);
	}, []);

	const handleApplyWrapped = useCallback(() => {
		setFlag(I18N_PSEUDO_WRAPPED_KEY, isWrapped);
		window.location.reload();
	}, [isWrapped]);

	const hasWrappedChanged = isWrapped !== getFlag(I18N_PSEUDO_WRAPPED_KEY);

	const handleLanguageChange = useCallback(
		(option: SelectSingleOption | null) => {
			if (option) {
				const nextLanguage = String(option.value);

				// Persist so the choice survives a page reload. `cimode` is a
				// dev-only mode and shouldn't be persisted as a user preference.
				if (isCiMode(nextLanguage)) {
					localStorage.removeItem(I18N_LANGUAGE_STORAGE_KEY);
				} else {
					localStorage.setItem(
						I18N_LANGUAGE_STORAGE_KEY,
						nextLanguage,
					);
				}

				i18n.changeLanguage(nextLanguage);
			}
		},
		[],
	);

	const handleToggleNamespaces = useCallback(() => {
		setShowNamespaces((prev) => {
			const next = !prev;

			setFlag(I18N_CIMODE_NS_KEY, next);
			i18n.options.appendNamespaceToCIMode = next;

			// force re-render of translated strings
			if (isCiMode(i18n.language)) {
				i18n.changeLanguage('cimode');
			}

			return next;
		});
	}, []);

	const selectedLanguage = useMemo(
		() => LANGUAGE_OPTIONS.find((lng) => lng.value === currentLng),
		[currentLng],
	);

	if (!isVisible) return null;

	const panelHeading = (
		<Row between="sm" middle="sm">
			<Heading headingSize={6}>i18n Dev Tools</Heading>
			<Row middle="sm">
				<Button
					ariaLabel={
						isCollapsed ? 'Expand dev panel' : 'Collapse dev panel'
					}
					icon={
						isCollapsed
							? 'ArrowDropUpLineIcon'
							: 'ArrowDownSLineIcon'
					}
					onClick={handleToggleCollapsed}
					iconSize="sm"
					variant="tertiary"
				/>
				<Button
					ariaLabel="Close dev panel"
					icon="CloseLineIcon"
					onClick={handleClose}
					iconSize="sm"
					variant="tertiary"
				/>
			</Row>
		</Row>
	);

	return (
		<StyledPanelWrapper $isCollapsed={isCollapsed}>
			<rd-theme-provider data-theme={currentTheme}>
				<Card heading={panelHeading} isClickable={false}>
					{!isCollapsed && (
						<StyledCardContent>
							<Tooltip
								content={`enableLanguageConfiguration LD flag is ${
									isLanguageConfigEnabled ? 'on' : 'off'
								}`}
							>
								<Tag
									size="sm"
									variant={
										isLanguageConfigEnabled
											? 'green'
											: 'grey'
									}
								>
									{`Language config: ${
										isLanguageConfigEnabled ? 'on' : 'off'
									}`}
								</Tag>
							</Tooltip>

							<SelectSingle
								isSearchable
								onChange={handleLanguageChange}
								options={LANGUAGE_OPTIONS}
								placeholder="Select language…"
								value={selectedLanguage}
								variant="primary"
							/>

							{isCiModeActive && (
								<Tooltip content="Prefix translation keys with their namespace in cimode">
									<Checkbox
										callback={handleToggleNamespaces}
										id="i18n-cimode-ns-toggle"
										isChecked={showNamespaces}
										label="Show namespaces"
									/>
								</Tooltip>
							)}

							{isPseudoActive && (
								<>
									<Tooltip content="Wrap pseudo text in brackets to preserve original length">
										<Checkbox
											callback={handleToggleWrapped}
											id="i18n-pseudo-wrapped-toggle"
											isChecked={isWrapped}
											label="Wrapped"
										/>
									</Tooltip>

									<StyledApplyButton
										disabled={!hasWrappedChanged}
										onClick={handleApplyWrapped}
										size="sm"
										variant="primary"
									>
										Apply
									</StyledApplyButton>
								</>
							)}
						</StyledCardContent>
					)}
				</Card>
			</rd-theme-provider>
		</StyledPanelWrapper>
	);
};

export default I18nDevPanel;
